import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Role } from '@unit/shared';
import { useAuth } from '../features/auth/AuthProvider.js';
import { useClaimRoles } from '../features/auth/useClaimRoles.js';
import { useMyProfile } from '../features/auth/useMyProfile.js';
import { NotificationsBell } from '../features/notifications/NotificationsBell.js';
import { PersonName } from './PersonName.js';
import { COMMANDER_ROLES, PEOPLE_ROLES, STOREROOM_ROLES, PERSONNEL_ROLES } from './RequireRole.js';

interface NavItem {
  to: string;
  labelKey: string;
  /** Undefined means every signed-in user sees it. */
  allow?: readonly Role[];
}

interface NavGroup {
  /** Undefined for the first, untitled group. */
  labelKey?: string;
  items: NavItem[];
}

/**
 * Only routes that actually exist appear here. A nav entry pointing at a screen
 * built two stories from now is a dead end the user has to learn to ignore.
 *
 * Grouped the way people think about the app (owner, 2026-09-13, side menu):
 * what is mine, what I command, and the storeroom. A group with no visible
 * item for this caller's roles is not rendered at all.
 */
const NAV: NavGroup[] = [
  {
    items: [
      { to: '/', labelKey: 'nav.home' },
      { to: '/me', labelKey: 'nav.me' },
      { to: '/forms', labelKey: 'nav.forms' },
      { to: '/transfers', labelKey: 'nav.transfers' },
    ],
  },
  {
    labelKey: 'nav.groups.command',
    items: [
      { to: '/dashboard', labelKey: 'nav.dashboard', allow: COMMANDER_ROLES },
      { to: '/org', labelKey: 'nav.org', allow: PEOPLE_ROLES },
      { to: '/profile-requests', labelKey: 'nav.profileRequests', allow: PERSONNEL_ROLES },
      { to: '/alerts', labelKey: 'nav.alerts', allow: COMMANDER_ROLES },
      { to: '/missions', labelKey: 'nav.missions', allow: COMMANDER_ROLES },
      { to: '/platoon-store', labelKey: 'nav.platoonStore', allow: ['platoon_sergeant'] },
      // The ledger, scoped to the commander's reach (story 41), sits with the command screens.
      { to: '/ledger', labelKey: 'nav.ledger', allow: COMMANDER_ROLES },
      { to: '/exports', labelKey: 'nav.exports', allow: COMMANDER_ROLES },
    ],
  },
  {
    labelKey: 'nav.groups.storeroom',
    items: [
      { to: '/inventory', labelKey: 'nav.inventory', allow: STOREROOM_ROLES },
      { to: '/templates', labelKey: 'nav.templates', allow: STOREROOM_ROLES },
      { to: '/import', labelKey: 'nav.import', allow: ['admin'] },
      { to: '/registrations', labelKey: 'nav.registrations', allow: ['admin'] },
      { to: '/settings', labelKey: 'nav.settings', allow: ['admin'] },
    ],
  },
];

/**
 * The application frame (owner, 2026-09-13: a side menu, and room for data
 * on a big screen).
 *
 * Desktop (`lg` and up): a sidebar on the start side holds the navigation;
 * the header and the routed screen take the rest of the width — no
 * max-width, the tables are what the screen is for. Below `lg` the same
 * navigation is a drawer behind a menu button in the header, closed on
 * every navigation.
 *
 * Layout is written with logical properties throughout (`ms-*`, `ps-*`,
 * `start`) rather than left/right, so the whole frame mirrors when `dir`
 * flips to RTL without a second stylesheet (REQUIREMENTS §2.8).
 */
export function AppLayout() {
  const { t } = useTranslation();
  const { signOut, user } = useAuth();
  const { roles } = useClaimRoles();
  const profile = useMyProfile(user?.uid ?? null, roles);
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // A route change closes the drawer; so does Escape.
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const groups = NAV.map((g) => ({
    ...g,
    items: g.items.filter((item) => !item.allow || roles.some((r) => item.allow?.includes(r))),
  })).filter((g) => g.items.length > 0);
  const hasNav = groups.reduce((n, g) => n + g.items.length, 0) > 1;

  // Who is signed in, above the menu (owner, 2026-09-20): the name with the
  // rank beside it, and the title under it when there is one.
  const me = profile.kind === 'ready' && (
    <div className="mb-5 border-b border-slate-100 px-3 pb-4" data-testid="nav-user">
      <p className="text-sm font-medium text-slate-900">
        <PersonName person={profile.profile} />
      </p>
      {profile.profile.title && <p className="text-xs text-slate-500">{profile.profile.title}</p>}
    </div>
  );

  const nav = (
    <nav aria-label={t('nav.label')} data-testid="main-nav" className="flex flex-col gap-5">
      {groups.map((g, i) => (
        <div key={g.labelKey ?? i}>
          {g.labelKey && (
            <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{t(g.labelKey)}</p>
          )}
          <ul className="flex flex-col gap-0.5">
            {g.items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `block rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                      isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`
                  }
                >
                  {t(item.labelKey)}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-full w-full">
      {hasNav && (
        <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-e border-slate-200 bg-white px-3 py-5 lg:flex" data-testid="sidebar">
          <div className="mb-6 px-3">
            <h1 className="text-lg font-semibold text-slate-900">{t('app.title')}</h1>
          </div>
          <div className="flex-1 overflow-y-auto">
            {me}
            {nav}
          </div>
        </aside>
      )}

      {hasNav && open && (
        <div className="fixed inset-0 z-40 lg:hidden" data-testid="nav-drawer">
          <button type="button" aria-label={t('nav.close')} onClick={() => setOpen(false)} className="absolute inset-0 bg-slate-900/40" />
          <div className="absolute inset-y-0 start-0 flex w-72 max-w-[85vw] flex-col bg-white px-3 py-5 shadow-xl">
            <div className="mb-6 flex items-start justify-between px-3">
              <h1 className="text-lg font-semibold text-slate-900">{t('app.title')}</h1>
              <button type="button" onClick={() => setOpen(false)} aria-label={t('nav.close')} className="rounded-lg px-2 py-1 text-slate-500 hover:bg-slate-100">
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {me}
              {nav}
            </div>
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            {hasNav && (
              <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label={t('nav.open')}
                data-testid="nav-toggle"
                className="rounded-lg px-2 py-1 text-xl leading-none text-slate-700 ring-1 ring-slate-300 hover:bg-slate-100 lg:hidden"
              >
                ☰
              </button>
            )}
            <div className="min-w-0 lg:hidden">
              <h1 className="truncate text-base font-semibold text-slate-900">{t('app.title')}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationsBell />
            <button
              type="button"
              onClick={() => void signOut()}
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 ring-1 ring-slate-300 transition hover:bg-slate-100"
            >
              {t('auth.signOut')}
            </button>
          </div>
        </header>

        <main className="flex flex-1 flex-col gap-6 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
